import { Button, Icon, Typography } from '@/components';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ASSETS_BASE_URL } from '@/config';
import { t } from 'i18next';
import { useCallback } from 'react';
import { OfferDetails } from '../../types';
import { useTranslation } from 'react-i18next';
import { getBuyerTypeLabel } from '../../helpers';

type SubmittedEoiCardProps = {
  readonly offer: OfferDetails;
};

export function SubmittedEoiCard({ offer }: SubmittedEoiCardProps) {
  const buyerTypeLabel = getBuyerTypeLabel(offer?.buyerType, t);

  const handleDownloadEoiFile = () => {
    if (!offer?.submittedEoiFile) return;
    const fileUrl = `${ASSETS_BASE_URL}/${offer.submittedEoiFile}`;
    window.open(fileUrl, '_blank');
  };

  const showLinkIcon = offer.buyerWebsite?.startsWith('https://') ?? false;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          <Typography as="h3" weight="semibold" size="title" className="uppercase">
            {t('Submitted Expression of Interest')}
          </Typography>
        </CardTitle>
        {offer?.submittedEoiFile && (
          <Button color="ghostPrimary" size="sm" onClick={handleDownloadEoiFile}>
            <Icon name="download" className="h-4 w-4" />
            Open PDF
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6 break-words">
        <div>
          <h3 className="text-grey-900 mb-3 font-semibold">{t('General Details')}</h3>
          <div className="space-y-4">
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">Express your interest:</div>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {offer.expressInterest ? offer.expressInterest : 'No info provided'}
              </Typography>
            </div>
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">Formal Expression of Interest:</div>
              <div className="text-grey-600 text-sm">
                {offer?.eoiFile ? (
                  <a
                    href={`${ASSETS_BASE_URL}/${offer.eoiFile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-700"
                  >
                    {t('Press to see the document')}
                  </a>
                ) : (
                  <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                    {t('No document provided')}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-grey-900 mb-3 font-semibold">Prospective Buyer Information</h3>
          <div className="space-y-4">
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">Role in Transaction:</div>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {buyerTypeLabel}
              </Typography>
            </div>
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">Final Buyer Description:</div>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {offer?.buyerComments}
              </Typography>
            </div>
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">{t('Corporate Presentation:')}</div>
              <div className="text-grey-600 text-sm">
                {offer?.buyerPresentation ? (
                  <a
                    href={`${ASSETS_BASE_URL}/${offer.buyerPresentation}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-700"
                  >
                    {t('Press to see the document')}
                  </a>
                ) : (
                  <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                    {t('No document provided')}
                  </Typography>
                )}
              </div>
            </div>
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">{t('Corporate website')}</div>
              <div className="text-grey-600 text-sm">
                {offer?.buyerWebsite ? (
                  <div className="flex items-center">
                    {offer?.buyerWebsite}{' '}
                    {showLinkIcon && (
                      <a
                        href={offer.buyerWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-700 ml-2"
                      >
                        <Icon name="external-link" className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ) : (
                  <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                    {t('No info provided')}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-grey-900 mb-3 font-semibold">{t('Additional Information & Documents')}</h3>
          <div className="space-y-4">
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">{t('Additional Information:')}</div>
              <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                {offer?.comments ? offer?.comments : 'No info provided'}
              </Typography>
            </div>
            <div>
              <div className="text-grey-700 mb-1 text-sm font-medium">{t('Other relevant documents')}</div>
              <div className="text-grey-600 text-sm">
                {offer?.file ? (
                  <Typography as="span" size="body-2">
                    <a
                      href={`${ASSETS_BASE_URL}/${offer.file}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-700"
                    >
                      {t('Press to see the document')}
                    </a>
                  </Typography>
                ) : (
                  <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
                    {t('No document provided')}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
